import React, { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  useGetAdjusterByIdQuery,
  usePatchAdjusterByIdMutation,
} from "services/adjuster-api";

import { useSelector } from "utils/redux";
import { getAdjusterForm } from "forms/adjuster";
import { isFetchBaseQueryError } from "utils/error-utils";

import InsurerTPAPageContainer from "../insurer/InsurerTPAPageContainer";
import KaboodleForm from "components/Forms/KaboodleForm";

import { setIsLoading } from "reducers/main";
import { useDispatch } from "utils/redux";

const AdjusterForm = () => {
  const dispatch = useDispatch();
  const { adjusterId } = useParams<{ adjusterId?: string }>();

  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const isReadOnly = !loggedInUser?.has_operations_authority;

  const {
    data: adjuster,
    isLoading,
    refetch: refetchAdjuster,
  } = useGetAdjusterByIdQuery(
    { id: Number(adjusterId) },
    { skip: !adjusterId },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const [patchAdjusterById] = usePatchAdjusterByIdMutation();

  const { reset, clearErrors, setError } = useFormContext();

  const constants = useSelector((state) => state.shared.constants);

  useEffect(() => {
    if (adjuster) {
      reset(adjuster);
    }
    refetchAdjuster();
  }, [adjuster, reset]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (isReadOnly) return;
      if (adjusterId) {
        try {
          await patchAdjusterById({
            id: Number(adjusterId),
            data: { [field]: value },
          }).unwrap();
          clearErrors(field);
        } catch (error: unknown) {
          if (isFetchBaseQueryError(error)) {
            const data = error.data as Record<string, string[]> | undefined;
            setError(field, {
              type: String(error.status),
              message: data?.[field]?.[0] || "Unable to save",
            });
          } else {
            setError(field, { message: "Unable to save" });
          }
        }
      }
    },
    [patchAdjusterById, adjusterId, clearErrors, setError, isReadOnly],
  );

  const adjusterFormContent = useMemo(() => {
    const forms = getAdjusterForm(constants, isReadOnly);
    return <KaboodleForm cards={forms[0].cards || []} onSave={handleSave} />;
  }, [constants, handleSave, isReadOnly]);

  return (
    <InsurerTPAPageContainer>{adjusterFormContent}</InsurerTPAPageContainer>
  );
};

export default AdjusterForm;
